import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Logo from './Logo';
import CustomLogo from './CustomLogo';


const Header = ({ theme, toggleTheme }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();


  const navigationItems = [
    { label: 'Home', path: '/landing-page', icon: 'Home' },
    { label: 'Features', path: '/features-page', icon: 'Zap' },
    { label: 'Pricing', path: '/pricing-page', icon: 'DollarSign' },
    { label: 'Demo', path: '/dashboard-demo', icon: 'Monitor' },
    { label: 'Contact', path: '/contact-page', icon: 'MessageCircle' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show header when scrolling up or at top
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } 
      // Hide header when scrolling down and not at top
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }
      
      setIsScrolled(currentScrollY > 10);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location?.pathname]);

  // Close mobile menu when header is hidden
  useEffect(() => {
    if (!isVisible) {
      setIsMobileMenuOpen(false);
    }
  }, [isVisible]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActivePath = (path) => {
    return location?.pathname === path || location?.pathname.startsWith(`${path}/`);
  };

  // Clean navigation without admin panel
  const visibleNavigationItems = navigationItems;

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-navigation transition-all duration-300 ${
          isVisible 
            ? 'translate-y-0' 
            : '-translate-y-full'
        } ${
          isScrolled 
            ? 'bg-background/95 backdrop-blur-sm shadow-lg border-b border-border' 
            : 'bg-background'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-16 nav-spacing">
            {/* Logo */}
            <CustomLogo design="custom" showText />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {visibleNavigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`relative px-3 py-2 text-sm font-medium transition-micro hover:text-primary ${
                    isActivePath(item?.path)
                      ? 'text-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {item?.label}
                  {isActivePath(item?.path) && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {theme && toggleTheme && (
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-full bg-muted text-muted-foreground hover:text-foreground transition-colors duration-200 ease-in-out"
                >
                  {theme === 'light' ? '🌙' : '☀️'}
                </button>
              )}
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <Link to="/login-page">Sign In</Link>
              </Button>
              <Button
                variant="default"
                size="sm"
                asChild
              >
                <Link to="/register-page">Get Started</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-background border-t border-border">
            <div className="px-4 py-2 space-y-1">
              {visibleNavigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`block px-3 py-2 text-base font-medium rounded-md transition-colors ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {item?.label}
                </Link>
              ))}
              
              <div className="pt-4 pb-2 space-y-2">
                {theme && toggleTheme && (
                  <button
                    onClick={toggleTheme}
                    className="w-full p-2 rounded-md bg-muted text-muted-foreground hover:text-foreground transition-colors text-left"
                  >
                    {theme === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
                  </button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  asChild
                >
                  <Link to="/login-page">Sign In</Link>
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  asChild
                >
                  <Link to="/register-page">Get Started</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;